import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const MeasurementModal = ({ isOpen, onClose, onSave, itemName, existingMeasurements, previousMeasurements }) => {
  const { t } = useTranslation();
  const [measurements, setMeasurements] = useState({
    length: '',
    chest: '',
    waist: '',
    hips: '',
    shoulder: '',
    sleeve: '',
    neck: '',
    special_notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      // Load existing measurements for this item, or previous customer measurements
      if (existingMeasurements) {
        setMeasurements(existingMeasurements);
      } else if (previousMeasurements) {
        setMeasurements(previousMeasurements);
      } else {
        // Reset to empty
        setMeasurements({
          length: '',
          chest: '',
          waist: '',
          hips: '',
          shoulder: '',
          sleeve: '',
          neck: '',
          special_notes: ''
        });
      }
    }
  }, [isOpen, existingMeasurements, previousMeasurements]);

  const handleSave = () => {
    onSave(measurements);
    onClose();
  };

  const handleLoadPrevious = () => {
    if (previousMeasurements) {
      setMeasurements(previousMeasurements);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">{t('measurements.customerMeasurements')}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('measurements.for')} {itemName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Info message */}
          {previousMeasurements && !existingMeasurements && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start justify-between">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-900">{t('measurements.previousFound')}</p>
                  <p className="text-sm text-blue-700 mt-1">{t('measurements.previousDescription')}</p>
                </div>
              </div>
              <button
                onClick={handleLoadPrevious}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                {t('measurements.loadPrevious')}
              </button>
            </div>
          )}

          {existingMeasurements && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
              <svg className="h-5 w-5 text-green-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-green-900">{t('measurements.measurementsSaved')}</p>
                <p className="text-sm text-green-700 mt-1">{t('measurements.canUpdate')}</p>
              </div>
            </div>
          )}

          {/* Measurement fields */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('measurements.length')}</label>
              <input
                type="number"
                step="0.01"
                value={measurements.length}
                onChange={(e) => setMeasurements({ ...measurements, length: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('measurements.chest')}</label>
              <input
                type="number"
                step="0.01"
                value={measurements.chest}
                onChange={(e) => setMeasurements({ ...measurements, chest: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('measurements.waist')}</label>
              <input
                type="number"
                step="0.01"
                value={measurements.waist}
                onChange={(e) => setMeasurements({ ...measurements, waist: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('measurements.hips')}</label>
              <input
                type="number"
                step="0.01"
                value={measurements.hips}
                onChange={(e) => setMeasurements({ ...measurements, hips: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('measurements.shoulder')}</label>
              <input
                type="number"
                step="0.01"
                value={measurements.shoulder}
                onChange={(e) => setMeasurements({ ...measurements, shoulder: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('measurements.sleeve')}</label>
              <input
                type="number"
                step="0.01"
                value={measurements.sleeve}
                onChange={(e) => setMeasurements({ ...measurements, sleeve: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('measurements.neck')}</label>
              <input
                type="number"
                step="0.01"
                value={measurements.neck}
                onChange={(e) => setMeasurements({ ...measurements, neck: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('measurements.specialNotes')}</label>
            <textarea
              value={measurements.special_notes}
              onChange={(e) => setMeasurements({ ...measurements, special_notes: e.target.value })}
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('measurements.specialNotesPlaceholder')}
            />
          </div>
        </div>

        {/* Footer actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
          >
            {t('measurements.saveMeasurements')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MeasurementModal;
